/**
 * Token price utilities using DexScreener API
 *
 * DexScreener provides real-time prices from on-chain DEX liquidity pools.
 * Used as a cross-check when other price sources (LiFi) may have stale data.
 */

// Simple in-memory cache to avoid hammering DexScreener on rapid quote refreshes
interface PriceCacheEntry {
  price: number;
  timestamp: number;
}

const priceCache = new Map<string, PriceCacheEntry>();
const CACHE_TTL_MS = 30 * 1000; // 30 seconds

/**
 * Fetch current USD price for a Solana token from DexScreener
 * Returns null if price unavailable or on error
 */
export async function fetchSolanaTokenPrice(
  mintAddress: string
): Promise<number | null> {
  if (!mintAddress) return null;

  // Check cache first
  const cached = priceCache.get(mintAddress);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
    return cached.price;
  }

  try {
    const response = await fetch(
      `https://api.dexscreener.com/tokens/v1/solana/${mintAddress}`,
      {
        headers: { Accept: "application/json" },
        // Short timeout to not block the UI
        signal: AbortSignal.timeout(5000),
      }
    );

    if (!response.ok) return null;

    const pairs: any[] = await response.json();

    // DexScreener returns array of trading pairs, take the first valid price
    for (const pair of pairs) {
      const price = Number(pair?.priceUsd || 0);
      if (Number.isFinite(price) && price > 0) {
        // Cache the result
        priceCache.set(mintAddress, { price, timestamp: Date.now() });
        return price;
      }
    }

    return null;
  } catch {
    // Network error, timeout, etc. - return null to fall back to LiFi
    return null;
  }
}

/**
 * Fetch prices for multiple Solana tokens in a single request
 * Returns Record<mintAddress, priceUSD>
 */
export async function fetchSolanaTokenPrices(
  mintAddresses: string[]
): Promise<Record<string, number>> {
  if (mintAddresses.length === 0) return {};

  const prices: Record<string, number> = {};
  const uncachedMints: string[] = [];

  // Check cache first
  for (const mint of mintAddresses) {
    const cached = priceCache.get(mint);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
      prices[mint] = cached.price;
    } else {
      uncachedMints.push(mint);
    }
  }

  // Fetch uncached prices
  if (uncachedMints.length > 0) {
    try {
      const response = await fetch(
        `https://api.dexscreener.com/tokens/v1/solana/${uncachedMints.join(",")}`,
        {
          headers: { Accept: "application/json" },
          signal: AbortSignal.timeout(5000),
        }
      );

      if (response.ok) {
        const pairs: any[] = await response.json();
        const seen = new Set<string>();

        for (const pair of pairs) {
          const mint = pair?.baseToken?.address;
          const price = Number(pair?.priceUsd || 0);
          if (mint && !seen.has(mint) && Number.isFinite(price) && price > 0) {
            prices[mint] = price;
            priceCache.set(mint, { price, timestamp: Date.now() });
            seen.add(mint);
          }
        }
      }
    } catch {
      // Ignore errors, return any prices already collected
    }
  }

  return prices;
}
